import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiChevronRight } from 'react-icons/fi';
import { getPages } from '../api/pagesApi';
import { PageRow } from '../components/dashboard/PagesList';
import { partitionPages, SOLUTION_DETAIL_PAGES_PATH, MARKET_DETAIL_PAGES_PATH, WEALTH_DETAIL_PAGES_PATH, MARKET_ANALYSIS_POST_PAGES_PATH } from '../utils/pageGroups';

const PagesPage = () => {
  const [mainPages, setMainPages] = useState([]);
  const [solutionDetailCount, setSolutionDetailCount] = useState(0);
  const [marketDetailCount, setMarketDetailCount] = useState(0);
  const [wealthDetailCount, setWealthDetailCount] = useState(0);
  const [marketAnalysisPostCount, setMarketAnalysisPostCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      try {
        const loadedPages = await getPages();
        if (!isMounted) {
          return;
        }

        const { mainPages: pages, solutionDetailPages, marketDetailPages, wealthDetailPages, marketAnalysisPostPages } = partitionPages(loadedPages);
        setMainPages(pages);
        setSolutionDetailCount(solutionDetailPages.length);
        setMarketDetailCount(marketDetailPages.length);
        setWealthDetailCount(wealthDetailPages.length);
        setMarketAnalysisPostCount(marketAnalysisPostPages.length);
        setError('');
      } catch (err) {
        if (!isMounted) {
          return;
        }

        setMainPages([]);
        setSolutionDetailCount(0);
        setMarketDetailCount(0);
        setWealthDetailCount(0);
        setMarketAnalysisPostCount(0);
        setError(err.message || 'Failed to load pages.');
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, []);

  if (isLoading) {
    return <p className="text-sm text-[#6b635c]">Loading pages…</p>;
  }

  if (error) {
    return (
      <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
        {error}
      </p>
    );
  }

  return (
    <div className="mx-auto max-w-3xl">
      <header className="mb-6 border-b border-[#e4d9c8] pb-5">
        <p className="text-[12px] font-medium uppercase tracking-[0.16em] text-[#6b635c]">
          Pages
        </p>
        <h2 className="mt-2 font-[Merriweather] text-[1.5rem] text-[#130705]">
          Website pages
        </h2>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[#6b635c]">
          Choose a page group or open a page to edit its sections.
        </p>
      </header>

      <div className="space-y-6">
        <section>
          <p className="mb-3 text-[12px] font-medium uppercase tracking-[0.14em] text-[#a8a098]">
            Main pages
          </p>
          {mainPages.length === 0 ? (
            <p className="text-sm text-[#6b635c]">No pages found.</p>
          ) : (
            <ul className="space-y-2">
              {mainPages.map((page) => (
                <PageRow
                  key={page.id}
                  to={`/dashboard/pages/${page.slug}`}
                  title={page.name}
                  subtitle={page.description || page.slug}
                />
              ))}
            </ul>
          )}
        </section>

        <section>
          <p className="mb-3 text-[12px] font-medium uppercase tracking-[0.14em] text-[#a8a098]">
            Page groups
          </p>
          <ul className="space-y-2">
            <li>
              <Link
                to={SOLUTION_DETAIL_PAGES_PATH}
                className="flex items-center justify-between gap-4 rounded-md border border-[#e4d9c8] bg-white px-4 py-4 transition hover:border-[#c9b48a] hover:bg-[#faf8f4]"
              >
                <div>
                  <p className="text-sm font-semibold text-[#130705]">Solution pages</p>
                  <p className="mt-1 text-[13px] leading-relaxed text-[#6b635c]">
                    Individual solution detail pages such as Finance Consulting and Risk &
                    Resilience.
                  </p>
                  <p className="mt-2 text-[12px] font-medium text-[#785E32]">
                    {solutionDetailCount} pages
                  </p>
                </div>
                <FiChevronRight className="h-4 w-4 shrink-0 text-[#a8a098]" aria-hidden="true" />
              </Link>
            </li>
            <li>
              <Link
                to={MARKET_DETAIL_PAGES_PATH}
                className="flex items-center justify-between gap-4 rounded-md border border-[#e4d9c8] bg-white px-4 py-4 transition hover:border-[#c9b48a] hover:bg-[#faf8f4]"
              >
                <div>
                  <p className="text-sm font-semibold text-[#130705]">Market pages</p>
                  <p className="mt-1 text-[13px] leading-relaxed text-[#6b635c]">
                    Individual market detail pages such as Equities, ETFs, and Forex.
                  </p>
                  <p className="mt-2 text-[12px] font-medium text-[#785E32]">
                    {marketDetailCount} pages
                  </p>
                </div>
                <FiChevronRight className="h-4 w-4 shrink-0 text-[#a8a098]" aria-hidden="true" />
              </Link>
            </li>
            <li>
              <Link
                to={WEALTH_DETAIL_PAGES_PATH}
                className="flex items-center justify-between gap-4 rounded-md border border-[#e4d9c8] bg-white px-4 py-4 transition hover:border-[#c9b48a] hover:bg-[#faf8f4]"
              >
                <div>
                  <p className="text-sm font-semibold text-[#130705]">Wealth product pages</p>
                  <p className="mt-1 text-[13px] leading-relaxed text-[#6b635c]">
                    Individual wealth product pages such as Active Saving Account and ISA Solutions.
                  </p>
                  <p className="mt-2 text-[12px] font-medium text-[#785E32]">
                    {wealthDetailCount} pages
                  </p>
                </div>
                <FiChevronRight className="h-4 w-4 shrink-0 text-[#a8a098]" aria-hidden="true" />
              </Link>
            </li>
            <li>
              <Link
                to={MARKET_ANALYSIS_POST_PAGES_PATH}
                className="flex items-center justify-between gap-4 rounded-md border border-[#e4d9c8] bg-white px-4 py-4 transition hover:border-[#c9b48a] hover:bg-[#faf8f4]"
              >
                <div>
                  <p className="text-sm font-semibold text-[#130705]">Market analysis posts</p>
                  <p className="mt-1 text-[13px] leading-relaxed text-[#6b635c]">
                    Individual research articles with images, avatars, and featured post support.
                  </p>
                  <p className="mt-2 text-[12px] font-medium text-[#785E32]">
                    {marketAnalysisPostCount} posts
                  </p>
                </div>
                <FiChevronRight className="h-4 w-4 shrink-0 text-[#a8a098]" aria-hidden="true" />
              </Link>
            </li>
          </ul>
        </section>
      </div>
    </div>
  );
};

export default PagesPage;
